<script setup>
import { ref, computed, onMounted, watch, nextTick } from "vue";
import { useRouter, useRoute } from "vue-router";
import ProgressBar from "./survey/ProgressBar.vue";
import { AuthService } from "@/services/authService";
import { doc, setDoc, getDoc } from "firebase/firestore";
import { db } from "@/config/firebase";
import UserProfileDropdown from "./UserProfileDropdown.vue";

const router = useRouter();
const route = useRoute();
const email = ref("");
const isAuthenticated = ref(false);
const currentUser = ref(null);
const authLoading = ref(true); // Track authentication loading state
const testMode = ref(false);

onMounted(() => {
  if (typeof window !== "undefined") {
    testMode.value = !!window.__SURVEY_TEST_MODE__;
  }
});

const surveyData = ref({
  // Email stage data
  email: "",

  // Personal Info stage data
  firstName: "",
  lastName: "",

  // Residency & Work Arrangements stage data
  residencyCounty: "",
  workArrangement: "",
  commutingFrequency: "",
  officeLocation: "",

  // Location & Work Arrangements stage data
  primaryLocation: "",
  crossBorderEmployment: "",
  internationalLocation: "",

  // Experience stage data
  currentRole: "",
  cyberExperience: "",
  totalExperience: "",
  seniorityLevel: "",
  organizationType: "",
  industrySector: "",

  // Career Pathways stage data
  upskillingSources: [],
  certificationCount: "",
  certificationBodies: [],

  // Demographics stage data
  ageRange: "",
});

const stages = [
  { path: "contact-info", title: "Contact Information" },
  {
    path: "residency-work-arrangements",
    title: "Residency & Work Arrangements",
  },
  { path: "competencies", title: "Professional Competencies" },
  { path: "experience", title: "Professional Experience" },
  { path: "career-pathways", title: "Career Pathways" },
  { path: "demographics", title: "Demographics" },
  { path: "thank-you", title: "Thank You" },
];

const currentStageIndex = computed(() => {
  const currentPath = route.path.split("/").pop();
  return stages.findIndex((stage) => stage.path === currentPath);
});

const progress = computed(() => {
  return (currentStageIndex.value / (stages.length - 1)) * 100;
});

const scrollToTop = (behavior = "smooth") => {
  if (typeof window === "undefined") return;

  // Window/document scroll for normal page flow (if enabled)
  window.scrollTo({ top: 0, left: 0, behavior });
  if (document?.documentElement && typeof document.documentElement.scrollTo === "function") {
    document.documentElement.scrollTo({ top: 0, left: 0, behavior });
  }
  if (document?.body && typeof document.body.scrollTo === "function") {
    document.body.scrollTo({ top: 0, left: 0, behavior });
  }

  // Also scroll any inner scroll containers used by different views/layouts
  const selectors = [
    ".survey-container",
    ".survey-form",
    ".game-survey",
    ".game-container",
    ".competencies-section",
    ".dropped-competencies",
    ".view-content",
    ".summary-layout",
    ".summary-content",
    ".mobile-content",
    ".competencies-list",
  ];
  selectors.forEach((selector) => {
    const el = document.querySelector(selector);
    if (el && typeof el.scrollTo === "function") {
      el.scrollTo({ top: 0, left: 0, behavior });
    }
  });
};

const nextStageInfo = computed(() => {
  const nextIndex = currentStageIndex.value + 1;
  if (currentStageIndex.value >= stages.length - 1) {
    return {
      title: "Finish",
      number: stages.length,
    };
  }
  return {
    title: stages[nextIndex].title,
    number: nextIndex + 1,
  };
});

const handleNextStage = async () => {
  // Prevent navigation from Thank You stage
  if (currentStageIndex.value >= stages.length - 1) {
    console.log("SurveyContainer: Cannot navigate from Thank You stage");
    return;
  }

  if (currentStageIndex.value < stages.length - 1) {
    const nextStage = stages[currentStageIndex.value + 1];

    // Skip authentication checks in test mode
    if (!testMode.value) {
      // Check if user is trying to navigate beyond the first stage without authentication
      const firebaseUser = AuthService.getCurrentUser();
      const isUserAuthenticated = isAuthenticated.value || firebaseUser;

      // Allow navigation to the first stage (contact-info) without authentication
      // But require authentication for all other stages
      if (currentStageIndex.value > 0 && !isUserAuthenticated) {
        console.log(
          "SurveyContainer: Blocking navigation - user not authenticated"
        );
        console.log("Current stage:", currentStageIndex.value);
        console.log("Local auth state:", isAuthenticated.value);
        console.log("Firebase auth state:", firebaseUser);
        alert(
          "You must be signed in to continue the survey. Please complete the authentication in the first step."
        );
        return;
      }

      // Check if next stage is thank-you and user is not authenticated
      if (nextStage.path === "thank-you" && !isUserAuthenticated) {
        console.log(
          "SurveyContainer: Blocking navigation to thank-you stage - user not authenticated"
        );
        console.log("Local auth state:", isAuthenticated.value);
        console.log("Firebase auth state:", firebaseUser);
        // Show error message to user
        alert(
          "You must be signed in to complete the survey. Please sign in and try again."
        );
        return;
      }
    }

    await router.push({
      path: `/survey/${nextStage.path}`,
      query: route.query,
    });
    saveProgress();
    // Scroll to top after navigation completes and DOM updates
    await nextTick();
    scrollToTop("smooth");

    console.log("Survey completed!");
  }
};

const handleSkipStage = () => {
  handleNextStage();
};

const handleUserAuthenticated = () => {
  console.log(
    "SurveyContainer: User authenticated event received from ContactInfoStage"
  );
  // Force a check of the current authentication state
  const currentUser = AuthService.getCurrentUser();
  if (currentUser) {
    isAuthenticated.value = true;
    currentUser.value = currentUser;
    email.value = currentUser.email;
    surveyData.value.email = currentUser.email;
  }
};

const saveProgress = async () => {
  if (testMode.value) {
    // Save to localStorage in test mode
    try {
      const key = "surveyTestResponses";
      const existing = JSON.parse(localStorage.getItem(key) || "[]");
      existing.push({
        ...surveyData.value,
        currentStage: currentStageIndex.value,
        lastUpdated: new Date().toISOString(),
        timestamp: new Date().toISOString(), // Add timestamp for retention tracking
      });
      localStorage.setItem(key, JSON.stringify(existing));
    } catch (error) {
      console.error(
        "Error saving progress to localStorage (test mode):",
        error
      );
    }
    return;
  }
  if (!isAuthenticated.value || !currentUser.value) return;

  try {
    await setDoc(
      doc(db, "users", currentUser.value.uid),
      {
        surveyData: surveyData.value,
        currentStage: currentStageIndex.value,
        lastUpdated: new Date(),
      },
      { merge: true }
    );
  } catch (error) {
    console.error("Error saving progress:", error);
  }
};

const loadProgress = async () => {
  if (!isAuthenticated.value || !currentUser.value) return;

  try {
    const userDoc = await getDoc(doc(db, "users", currentUser.value.uid));
    if (userDoc.exists()) {
      const data = userDoc.data();
      if (data.surveyData) {
        surveyData.value = { ...surveyData.value, ...data.surveyData };
        console.log("Survey so far: ", surveyData.value);
      }

      // Navigate to the saved stage if it exists
      if (
        data.currentStage !== undefined &&
        data.currentStage < stages.length
      ) {
        const stage = stages[data.currentStage];
        if (stage && route.path !== `/survey/${stage.path}`) {
          router.push(`/survey/${stage.path}`);
        }
      }
    }
  } catch (error) {
    console.error("Error loading progress:", error);
  }
};

const handleSignOut = async () => {
  const result = await AuthService.signOut();
  if (result.success) {
    router.push("/");
  }
};

const handleProfile = () => {
  // For now, just show an alert. This could be expanded to navigate to a profile page
  alert("Profile functionality coming soon!");
};

const handleSettings = () => {
  // For now, just show an alert. This could be expanded to navigate to a settings page
  alert("Settings functionality coming soon!");
};

const handleHelp = () => {
  // For now, just show an alert. This could be expanded to navigate to a help page
  alert("Help functionality coming soon!");
};

// Utility function to ensure buttons are visible
const ensureButtonVisibility = () => {
  // Simple scroll to top when navigating
  scrollToTop("smooth");
};

// Listen for auth state changes
onMounted(async () => {
  // Email link handling is now done at the app level in main.js
  // to prevent router guard race conditions

  // Skip auth state listener in test mode
  if (testMode.value) {
    authLoading.value = false;
    return;
  }

  AuthService.onAuthStateChanged(async (user) => {
    if (user) {
      isAuthenticated.value = true;
      currentUser.value = user;
      email.value = user.email;
      surveyData.value.email = user.email;

      // Load user data and progress
      await loadProgress();
    } else {
      isAuthenticated.value = false;
      currentUser.value = null;
      email.value = "";
      surveyData.value.email = "";
    }

    // Mark authentication as complete (whether user is authenticated or not)
    authLoading.value = false;
  });

  // Ensure buttons are visible when component mounts
  setTimeout(ensureButtonVisibility, 500);
});

// Watch for route changes to ensure button visibility and scroll to top
watch(
  () => route.path,
  async () => {
    // Wait for route-driven DOM updates before scrolling containers
    await nextTick();
    requestAnimationFrame(() => {
      ensureButtonVisibility();
      scrollToTop("smooth");
    });
  }
);
</script>

<template>
  <div
    :class="`survey-container ${
      stages[currentStageIndex].path === 'competencies' ? 'game-survey' : ''
    }`"
  >
    <!-- Test Mode Banner -->
    <div v-if="testMode" class="test-mode-banner">
      <strong>Test Mode:</strong> Survey responses will <u>not</u> be saved to
      the server. Data is stored only in your browser.
    </div>
    <!-- Authentication Loading Overlay -->
    <div v-if="authLoading" class="auth-loading-overlay">
      <div class="auth-loading-content">
        <div class="spinner"></div>
        <p>Checking authentication status...</p>
      </div>
    </div>

    <!-- Header with user info and sign out -->

    <ProgressBar :progress="progress" />
    <p class="stage-counter">
      Stage {{ currentStageIndex + 1 }} of {{ stages.length }}
    </p>

    <!-- Authentication Required Warning -->
    <div
      v-if="
        currentStageIndex > 0 &&
        !isAuthenticated &&
        !AuthService.getCurrentUser() &&
        !testMode
      "
      class="auth-warning"
    >
      <p>
        ⚠️ Authentication required to continue. Please return to the first step
        to sign in.
      </p>
    </div>

    <router-view
      v-model:email="email"
      v-model:surveyData="surveyData"
      @next-stage="handleNextStage"
      @skip-stage="handleSkipStage"
      @save-progress="saveProgress"
      @user-authenticated="handleUserAuthenticated"
      :nextStageInfo="nextStageInfo"
      :currentStage="currentStageIndex + 1"
      :totalStages="stages.length"
      :currentUser="currentUser"
    />

    <!-- Debug information - only show if not on Thank You stage -->
    <div
      v-if="currentStageIndex < stages.length - 1"
      class="debug-info"
      style="margin-top: 20px; font-size: 12px; color: #666; text-align: center"
    >
      <p>Next Stage: {{ nextStageInfo.title }}</p>
    </div>
  </div>
</template>

<style>
/* Global styles */
:root {
  --primary-color: #005461;
  --primary-hover: #9cd3d3;
  --secondary-color: #ea5d0a;
  --secondary-hover: #f8f9fa;
  --background-color: #dae2e9;
  --surface-color: #f8f8f8;
  --text-color: #2c3e50;
  --border-color: #ddd;
  --input-background: #ffffff;
}

/* Authentication Loading Overlay */
.auth-loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.95);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.auth-loading-content {
  text-align: center;
  padding: 2rem;
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
}

.auth-loading-content p {
  margin-top: 1rem;
  color: var(--text-color);
  font-size: 1rem;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid var(--primary-color);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.survey-container {
  width: 100%;
  max-width: 800px;
  margin: 1rem auto;
  margin-top: 5%;
  padding: 1.5rem;
  background-color: var(--surface-color);
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  box-sizing: border-box;
  /* Custom scrollbar styling to preserve rounded corners */
  scrollbar-width: thin;
  scrollbar-color: rgba(0, 0, 0, 0.3) transparent;
}

/* Webkit scrollbar styling for better appearance */
.survey-container::-webkit-scrollbar {
  width: 8px;
}

.survey-container::-webkit-scrollbar-track {
  background: transparent;
  border-radius: 12px;
}

.survey-container::-webkit-scrollbar-thumb {
  background-color: rgba(0, 0, 0, 0.3);
  border-radius: 4px;
  border: 2px solid var(--surface-color);
}

.survey-container::-webkit-scrollbar-thumb:hover {
  background-color: rgba(0, 0, 0, 0.5);
}

.survey-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  background-color: white;
  border-radius: 8px;
  margin-bottom: 1rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.user-info {
  font-weight: 500;
  color: var(--text-color);
}

.game-survey {
  width: 100%;
  max-width: 100%;
  margin: 0;
}

.stage-counter {
  text-align: center;
  color: var(--text-color);
  opacity: 0.8;
  margin: 0.75rem 0;
  font-size: 0.9rem;
}

.auth-warning {
  background-color: #fff3cd;
  color: #856404;
  padding: 1rem;
  border-radius: 8px;
  margin: 1rem 0;
  border: 1px solid #ffeaa7;
  text-align: center;
  font-size: 0.95rem;
}

.auth-warning p {
  margin: 0;
  font-weight: 500;
}

/* Common form styles */
.survey-form {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  margin-top: 1.5rem;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

label {
  font-weight: 500;
  color: var(--text-color);
  font-size: 0.95rem;
}

input,
select,
textarea {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid var(--border-color);
  border-radius: 8px;
  font-size: 1rem;
  background-color: var(--input-background);
  color: var(--text-color);
  transition: border-color 0.3s ease;
  box-sizing: border-box;
}

input:focus,
select:focus,
textarea:focus {
  outline: none;
  border-color: var(--primary-color);
  box-shadow: 0 0 0 2px rgba(66, 184, 131, 0.2);
}

button {
  width: 100%;
  max-width: 300px;
  margin: 0 auto;
  padding: 0.75rem 1.5rem;
  background-color: var(--primary-color);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  cursor: pointer;
  transition: background-color 0.3s ease;
}

button:hover {
  background-color: var(--primary-hover);
}

button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Checkbox styles */
.checkbox-group {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.checkbox-group label {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  font-weight: normal;
  cursor: pointer;
  min-height: 44px;
  padding: 0.5rem 0;
  line-height: 1.4;
}

.checkbox-group input[type="checkbox"] {
  width: 18px;
  height: 18px;
  cursor: pointer;
  margin-top: 0.125rem;
  flex-shrink: 0;
}

h2 {
  color: var(--text-color);
  margin-bottom: 1.25rem;
  text-align: center;
  font-size: clamp(1.5rem, 4vw, 2rem);
}

/* Transition animations */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Responsive breakpoints */
@media (max-width: 768px) {
  .survey-container {
    margin: 0.5rem auto;
    padding: 1rem;
    width: calc(100% - 1rem);
    /* Body/html scrolling is disabled globally; make the survey itself scroll */
    max-height: calc(100dvh - 1rem);
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
  }

  .survey-form {
    gap: 1rem;
    margin-top: 1.25rem;
    padding-bottom: 4rem; /* Ensure buttons are accessible and can scroll past */
  }

  input,
  select,
  textarea {
    padding: 0.6rem;
    font-size: 0.95rem;
  }

  button {
    padding: 0.6rem 1.25rem;
    font-size: 0.95rem;
  }
}

@media (max-width: 480px) {
  .survey-container {
    margin: 0.25rem auto;
    padding: 0.75rem;
    border-radius: 8px;
    width: calc(100% - 0.5rem);
      /* max-height: calc(100dvh - 0.5rem); */
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
  }

  .survey-form {
    gap: 0.75rem;
    margin-top: 1rem;
    padding-bottom: 5rem; /* Ensure buttons are accessible and can scroll past */
  }

  input,
  select,
  textarea {
    padding: 0.5rem;
    font-size: 0.9rem;
  }

  button {
    padding: 0.5rem 1rem;
    font-size: 0.9rem;
  }

  .checkbox-group {
    gap: 0.5rem;
  }
}

.skip-button {
  background-color: transparent;
  color: var(--text-color);
  border: 1px solid var(--border-color);
  margin-top: 0.5rem;
}

.skip-button:hover {
  background-color: var(--surface-color);
  border-color: var(--text-color);
}

.test-mode-banner {
  background: #ffe066;
  color: #333;
  padding: 1em;
  text-align: center;
  font-size: 1.1em;
  border-bottom: 2px solid #ffd700;
  margin-bottom: 1em;
}
</style>
